import React, { useEffect, useState } from 'react';
import { Container, makeStyles } from '@material-ui/core';
import Page from 'src/components/Page';
import useAuth from 'src/hooks/useAuth';
import { useServiceClient } from 'src/hooks/useServiceClient';
import { getDefaultServiceClientFilters } from './getDefaultServiceClientFilters';
import {
    buildSearchPayload,
    buildSearchQuery,
    getInitialFiltersByAccessLevel,
} from 'src/modules/serviceClients/helpers';
import { useTranslation } from 'react-i18next';
import { IServiceClientFilterValues } from './types';
import { Results } from './Results';
import { SERVICE_CLIENT_ADVANCED_FILTERS_KEY } from './constants';

const useStyles = makeStyles((theme) => ({
    root: {
        minHeight: '100%',
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(1),
    },
}));

export const ServiceClientsList = () => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { user } = useAuth();
    const { searchServiceClientsByFilters } = useServiceClient();
    const [filters, setFilters] = useState<IServiceClientFilterValues>(
        getDefaultServiceClientFilters()
    );
    const [isReady, setIsReady] = useState(false);

    const handleInitializeFilters = () => {
        const saved = localStorage.getItem(SERVICE_CLIENT_ADVANCED_FILTERS_KEY);
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                setFilters({ ...getDefaultServiceClientFilters(), ...parsed });
            } catch {
                setFilters(getDefaultServiceClientFilters());
            }
        } else {
            const response = getInitialFiltersByAccessLevel(user);
            if (!response?.success) {
                setFilters(getDefaultServiceClientFilters());
            }
        }
        setIsReady(true);
    };

    useEffect(() => {
        if (user?._id) handleInitializeFilters();
    }, [user?._id]);

    const loadInformation = () => {
        if (!isReady || !user?.accessLevel) return;
        const response = getInitialFiltersByAccessLevel(user);
        const finalQuery = buildSearchQuery(filters, response);
        const payload = buildSearchPayload(finalQuery);
        searchServiceClientsByFilters(payload, {
            isReadPermissionNecessary: true,
        });
        localStorage.setItem(
            SERVICE_CLIENT_ADVANCED_FILTERS_KEY,
            JSON.stringify(filters)
        );
    };

    useEffect(() => {
        if (isReady && user?._id) loadInformation();
    }, [filters, isReady]);

    return (
        <Page className={classes.root} title={t('serviceClients.Title')}>
            <Container maxWidth={false}>
                <Results
                    values={filters}
                    setValues={setFilters}
                    loadInformation={loadInformation}
                />
            </Container>
        </Page>
    );
};
